










































































































































































































































import { mapGetters } from "vuex";
import Vue from "vue";
import iwOptionCheckbox from "@/optimizedEncoding/components/packSelection/OptionCheckbox.vue";
import { resetPayment } from "@/shared/scripts/ValidateData";
import { PackOptionInformation } from "@/shared/interfaces/optimizedEncoding/PackSelection.interface";
import { billboardBlockedBy } from "@/optimizedEncoding/scripts/helpers/billboardEligibility";

export default Vue.extend({
    components: {
        iwOptionCheckbox: iwOptionCheckbox,
    },
    props: {
        action: {
            type: String,
            required: false,
            default: "publication",
        },
        isSelected: {
            type: Boolean,
            required: false,
            default: false
        },
        mostPopular: {
            type: Boolean,
            required: false,
            default: false
        },
        pack: {
            type: Object,
            required: true
        },
        checkboxResetVersion: {
            type: Number,
            required: false,
            default: 0
        },
        isWelcomePage: {
            type: Boolean,
            required: false,
            default: false
        },
        isCurrentPack: {
            type: Boolean,
            required: false,
            default: false
        },
        showImage: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data: function() {
        return {
            selectedPackageInStore: "publication.visualizationOption",
            selectedPackageStoreOrder: "order.visualizationOption",
            hasRecommendationClass: false,
            observer: null as MutationObserver | null,
        };
    },

    computed: {
        guaranteeContacts: function(): number {
            return ({ classic: 5, premium: 10 } as Record<string, number>)[this.pack.packType] || 0;
        },
        ...mapGetters(
            {"getValueTransaction": "FormModule/getValueTransaction"}
        ),

        // A billboard option is not rendered when it is blocked by the property
        // country (see OptionCheckbox `shouldHide`); excluding it here keeps the
        // section headings from showing above an empty list.
        isOptionHidden: function(): (opt: PackOptionInformation) => boolean {
            const billboardHidden = billboardBlockedBy() === "property";
            return (opt: PackOptionInformation) => opt.type === "billboard" && billboardHidden;
        },

        includedOptions: function(): PackOptionInformation[] {
            const infos = this.pack?.additionalOption?.optionInformations ?? [];
            return infos.filter((opt: PackOptionInformation) => opt.includedInPack === true && !this.isOptionHidden(opt));
        },

        additionalOptions: function(): PackOptionInformation[] {
            // The current pack's card is informational only (not something the customer
            // is choosing here), so paid add-ons like billboard/extension don't apply to it.
            if (this.isCurrentPack) {
                return [];
            }
            const infos = this.pack?.additionalOption?.optionInformations ?? [];
            const filtered = infos.filter((opt: PackOptionInformation) => !opt.includedInPack && !this.isOptionHidden(opt));
            if (!this.includedOptions.length) {
                return filtered.slice().reverse();
            }
            return filtered;
        },
        activePack: function(): string {
            return this.$store.getters["FormModule/getValueTransaction"]("publication.visualizationOption") || "";
        },
        additionalOptionInfo: function(): any | null {
            return this.pack?.additionalOption?.optionInformations?.[1] || null;
        },
        btnLabel: function(): string {
            const label =
                this.isSelected
                    ? this.$t("selected") as string
                    : this.$t("select") as string;
            return label;
        },
        hasAutomaticDiscount: function(): boolean {
            return this.pack?.automaticDiscount != null && this.pack.automaticDiscount < 0;
        },
        selectedDurationLabel: function(): string | null {
            const selectedPricing = this.getValueTransaction("transaction.selectedPricing");
            if (selectedPricing && selectedPricing.duration) {
                return this.pluralization(
                    `x_${selectedPricing.duration.unit.toLowerCase()}s`,
                    selectedPricing.duration.count
                );
            }
            const durationId = this.getValueTransaction("transaction.duration");
            const packages = this.getValueTransaction("transaction.packages");
            if (durationId && packages) {
                const defaultPricing = packages.find((p: any) => p.id === durationId);
                if (defaultPricing && defaultPricing.duration) {
                    return this.pluralization(
                        `x_${defaultPricing.duration.unit.toLowerCase()}s`,
                        defaultPricing.duration.count
                    );
                }
            }
            return null;
        },
        tag: function(): string {
            if (this.isCurrentPack) {
                return this.$t("current_pack") as string;
            }
            if (this.hasAutomaticDiscount) {
                return this.$t("promo") as string;
            }
            if (this.hasRecommendationClass && this.action !== "upgrade") {
                return this.$t("most_popular") as string;
            }

            return "";
        },
    },

    mounted: function() {
        window.addEventListener("resize", this.syncOptionsHeight);
        this.$nextTick(this.syncOptionsHeight);
        const cardRef = this.$refs.cardRoot as HTMLElement | undefined;
        if (cardRef) {
            this.hasRecommendationClass = cardRef.classList?.contains("pack-our-recommendation");
            this.observer = new MutationObserver(() => {
                this.hasRecommendationClass = cardRef.classList?.contains("pack-our-recommendation");
            });
            this.observer.observe(cardRef, { attributes: true, attributeFilter: ["class"] });
        }
    },

    updated: function() {
        this.syncOptionsHeight();
    },

    beforeDestroy: function() {
        window.removeEventListener("resize", this.syncOptionsHeight);
        this.stopObserving();
    },

    methods: {
        // Equalize header and option-list heights across the row, so the
        // guarantee box (right below the list) lines up across the packs.
        syncOptionsHeight: function(): void {
            const grid = (this.$refs.cardRoot as HTMLElement | undefined)?.parentElement;
            if (!grid) return;
            const cards = Array.from(grid.querySelectorAll(".pack-container")) as HTMLElement[];
            const stacked = new Set(cards.map(c => c.offsetTop)).size > 1; // mobile
            [".pack-header", ".pack-info__description__options"].forEach(selector => {
                const els = Array.from(grid.querySelectorAll(selector)) as HTMLElement[];
                els.forEach(e => e.style.minHeight = "");
                if (stacked) return;
                const max = Math.max(...els.map(e => e.offsetHeight));
                els.forEach(e => e.style.minHeight = max + "px");
            });
        },

        selectPackage: function(): void {
            this.$store.commit("OrdersModule/resetState");
            this.addToStore(this.pack.title);
        },

        addToStore: function(label: string): void {
            // Save the selected pack
            this.$store.commit("FormModule/addValueTransaction", {
                path: this.selectedPackageInStore,
                value: label,
            });

            // Save pack price (use discounted total if automatic discount exists)
            this.$store.commit("FormModule/addValueTransaction", {
                path: `optimizedEncoding.selectedPack.packPrice.${label}`,
                value: this.pack.automaticDiscount != null ? this.pack.packPriceTotal : this.pack.packPrice,
            });

            // Update order active pack
            this.$store.commit("FormModule/addValueOrder", {
                path: this.selectedPackageStoreOrder,
                value: label.toUpperCase(),
            });

            // Update extension price only for ACTIVE pack
            const selectedPackOptions = this.$store.getters["FormModule/getValueTransaction"](
                "optimizedEncoding.selectedPackOptions"
            ) || {};

            const activePackOptions = selectedPackOptions[label] || {};

            const ext = activePackOptions.extension || null;

            this.$store.commit("OrdersModule/setExtensionOption", ext);
            this.$store.commit("FormModule/addValueTransaction", {
                path: "transaction.pricing.extensionOption",
                value: ext,
            });

            const billboard = activePackOptions.billboard || null;
            this.$store.commit("FormModule/addValueTransaction", {
                path: "transaction.pricing.billboard",
                value: billboard,
            });

            // Reset contact information and payment sections
            resetPayment();
        },

        stopObserving: function(): void {
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }
        }
    }
});
